import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Archive, ChevronLeft } from 'lucide-react';
import { projectsAPI } from '../lib/api';
import { getUser } from '../lib/auth';
import { t } from '../lib/i18n';

const ProjectContext = createContext(null);
export const useProject = () => useContext(ProjectContext);

const TABS = [
  { to: '', label: 'Tasks', end: true },
  { to: 'roadmap', label: 'Roadmap' },
  { to: 'discussion', label: 'Discussion' },
  { to: 'docs', label: 'Docs' },
  { to: 'files', label: 'Files' },
  { to: 'forms', label: 'Forms' },
  { to: 'report', label: 'Report' },
  { to: 'activity', label: 'Activity' },
  // settings = project administration — not part of the Contributor surface
  { to: 'settings', label: 'Settings', hideForContributor: true },
];

export default function ProjectLayout() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [roster, setRoster] = useState([]);
  const [missing, setMissing] = useState(false);
  const user = getUser();

  const reload = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([projectsAPI.get(projectId), projectsAPI.roster()]);
      setProject(p.data);
      setRoster(r.data.members);
    } catch (err) {
      if (err.response?.status === 404) setMissing(true);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const archiveToggle = async () => {
    if (project.archived) await projectsAPI.unarchive(project.id);
    else await projectsAPI.archive(project.id);
    navigate('/');
  };

  if (missing) {
    return (
      <div className="py-16 text-center text-sm text-pm-muted">
        {t('Project not found.')}{' '}
        <button type="button" onClick={() => navigate('/')} className="text-pm-accent underline">
          {t('Back to projects')}
        </button>
      </div>
    );
  }
  if (!project) return null;

  return (
    <ProjectContext.Provider value={{ project, roster, reload }}>
      <div className="flex h-full flex-col">
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded p-1 text-pm-muted hover:bg-pm-raised hover:text-pm-text"
            title={t('All projects')}
          >
            <ChevronLeft size={18} />
          </button>
          <span
            className="flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold"
            style={{ background: `${project.color}26`, color: project.color }}
          >
            {(project.icon || project.name[0] || '?').slice(0, 2).toUpperCase()}
          </span>
          <h1 className="min-w-0 truncate text-base font-semibold">{project.name}</h1>
          {project.archived && (
            <span className="rounded-full bg-pm-raised px-2 py-0.5 text-xs text-pm-muted">{t('Archived')}</span>
          )}
          <nav className="ml-4 flex gap-1">
            {TABS.filter((tab) => !(tab.hideForContributor && user?.pr === 'contributor')).map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `rounded-md px-2.5 py-1.5 text-sm ${
                    isActive ? 'bg-pm-raised text-pm-text' : 'text-pm-muted hover:text-pm-text'
                  }`
                }
              >
                {t(tab.label)}
              </NavLink>
            ))}
          </nav>
          {(user?.role === 'admin' || user?.role === 'member') && user?.pr !== 'contributor' && (
            <button
              type="button"
              onClick={archiveToggle}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-pm-faint hover:bg-pm-raised hover:text-pm-text"
            >
              <Archive size={13} />
              {project.archived ? t('Unarchive') : t('Archive')}
            </button>
          )}
        </div>
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
    </ProjectContext.Provider>
  );
}
